import React, { createContext, useState, useContext, useEffect } from 'react';
import WebFont from 'webfontloader';

interface ThemeContextType {
  primaryColor: string;
  secondaryColor: string;
  font: string;
  updateTheme: (theme: Partial<ThemeSettings>) => void;
  availableFonts: string[];
}

interface ThemeSettings {
  primaryColor: string;
  secondaryColor: string;
  font: string;
}

const defaultTheme: ThemeSettings = {
  primaryColor: '#4F46E5', // indigo-600
  secondaryColor: '#1F2937', // gray-800
  font: 'Inter',
};

const availableFonts = [
  'Inter',
  'Roboto',
  'Open Sans',
  'Lato',
  'Poppins',
  'Montserrat',
  'Source Sans Pro',
  'Raleway',
  'Ubuntu',
  'Playfair Display'
];

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<ThemeSettings>(() => {
    const savedTheme = localStorage.getItem('theme');
    return savedTheme ? JSON.parse(savedTheme) : defaultTheme;
  });

  useEffect(() => {
    // Load all fonts
    WebFont.load({
      google: {
        families: availableFonts
      }
    });

    // Apply theme
    document.documentElement.style.setProperty('--primary-color', theme.primaryColor);
    document.documentElement.style.setProperty('--secondary-color', theme.secondaryColor);
    document.documentElement.style.setProperty('--font-family', theme.font);
    
    // Save theme
    localStorage.setItem('theme', JSON.stringify(theme));
  }, [theme]);

  const updateTheme = (newTheme: Partial<ThemeSettings>) => {
    setTheme(prev => ({ ...prev, ...newTheme }));
  };

  return (
    <ThemeContext.Provider value={{ 
      ...theme, 
      updateTheme,
      availableFonts
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};